/**
 * The in-room sayings picker: a Say button in the action row, and the scroll list behind it.
 *
 * The player report it answers: "All users will get a pre-defined set of sayings which they can
 * select by a button which provides a scroll list of these pre-defined sayings they select from
 * ... This should be a registered user only option ... non-registered users should show the
 * sayings option but indicate they must be registered to use them."
 *
 * It lives outside `game/` because it reads two things nothing in that subtree may know about:
 * the session (which is what the guest gate is) and `sayings.list` (which is the player's own
 * order, and their approved customs, neither of which is knowable from the build). `routes/play.tsx`
 * is the one file that knows both tRPC and the game exist, and it is what hands the pick down to
 * `GameConnection`.
 *
 * Five rules:
 *
 * - **A pick is an ID.** `onSay` takes a saying id and nothing else; the server resolves the
 *   words. There is no text field on this screen and there must never be one — a saying that
 *   travelled as text would be a chat, which is the thing this whole feature is designed not to
 *   be. Submitting a *new* saying is the profile page's job, behind a moderator.
 * - **It says "sent", never "done".** `sentAtMs` is when the last pick went on the wire, not when
 *   a bubble appeared, so the cooldown drawn here is the client's own honest reading of
 *   SAYING_COOLDOWN_SECONDS rather than an acknowledgement. The server is what decides whether a
 *   saying was granted, and it refuses silently — which is exactly why the button has to say so.
 * - **A guest is refused out loud.** The button is drawn, the list is drawn, and every entry is
 *   inert above a link to registration. `sayings.list` is `protectedProcedure` and answers a guest
 *   with a FORBIDDEN, so the query is not made at all rather than made and shown as an error — the
 *   arrangement the profile page's photo library is already under.
 * - **The panel never fills the arena, and never freezes it either.** It used to be the app's own
 *   `Dialog` — Escape, the backdrop, the focus trap and the `role="dialog"` `keyboardActs` reads
 *   so a SPACE meant for the list does not also split the cluster. That is reversed here, for the
 *   reason the room roster's is (see `ArenaPanel`, and `PopulationBadge`): saying "gg" is
 *   something a player does *while being chased*, and a backdrop that eats every pointer sample
 *   before it reaches the canvas is the chase lost. It is an `ArenaPanel` now: translucent, capped
 *   at a fraction of the viewport, scrolled inside, and with the arena still steering all around
 *   it. It goes through a **portal** because a HUD zone can be an `overflow-hidden` column and
 *   an ancestor carrying `backdrop-blur` is the containing block for a `fixed` descendant — two
 *   different ways for the panel to end up clipped to the strip its button is standing in.
 * - **It opens on the tap, not on the touch.** The Say control is the one `ActionButton` in this
 *   client with `trigger="tap"`, because it opens a panel the rest of the same gesture would
 *   otherwise dismiss. The whole gesture belongs to the button, wherever the button is drawn —
 *   the touch action row and the HUD's bottom-left column are one component with one rule.
 */

import { DEFAULT_SAYINGS, SAYING_COOLDOWN_SECONDS } from '@chompdome/shared/constants';
import { type RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useRef } from 'react';
import { ActionButton } from '@/game/hud/ActionButtons';
import { anchorAbove, type AnchorPosition, isMeasured } from '@/game/hud/anchor';
import { ArenaPanel } from '@/game/hud/ArenaPanel';
import { useTapActivate } from '@/game/hud/tap-activate';
import { useCompactHud } from '@/game/hud/layout';
import { buttonVariants } from '@/components/ui/button';
import { useSession } from '@/hooks/use-session';
import { trpc } from '@/lib/trpc';

/** How long a pick locks the list, in milliseconds — the server's own cooldown, named here. */
export const SAYING_COOLDOWN_MS = SAYING_COOLDOWN_SECONDS * 1_000;

/** How often the countdown re-reads the clock while a pick is cooling down. */
const COOLDOWN_TICK_MS = 250;

/**
 * The panel's geometry against its own button, in CSS pixels — the numbers `hud/anchor.ts` does
 * the arithmetic with, kept here because they are this panel's taste rather than the module's.
 *
 * `PANEL_WIDTH_PX` is `w-72`, which stays on the element as the class so the fallback position
 * is the same width as the measured one. The two caps are what the panel used to carry as
 * `max-h-[52dvh]` and `max-h-[24rem]`; on a phone the room above the button is smaller than
 * either and is what actually decides it.
 */
const PANEL_WIDTH_PX = 288;
const PANEL_GAP_PX = 8;
const PANEL_MARGIN_PX = 12;
const COMPACT_PANEL_MAX_PX = 260;
const ROOMY_PANEL_MAX_PX = 384;

export interface SayingsPickerProps {
  /** Where a pick goes: `GameConnection.say`, through the route. An id, never words. */
  onSay: (sayingId: string) => void;
  /** When our last pick left on an input frame, or null. See `HudSnapshot.sayingSentAtMs`. */
  sentAtMs: number | null;
  /** The footer's controls are smaller than the overlay's; the picker matches whichever row it is in. */
  size?: 'sm' | 'md' | 'lg';
}

export function SayingsPicker({ onSay, sentAtMs, size = 'md' }: SayingsPickerProps) {
  // Where the panel may reach on this surface. `Hud` reads the same hook for its own zones, so
  // the picker and the row it is standing in cannot disagree about which layout is up.
  const compact = useCompactHud();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const at = useAnchoredAbove(buttonRef, open, compact);
  const { user } = useSession();
  // Signed out and guest are the same answer here: neither may speak, and neither is asked.
  const canSpeak = user !== null && !user.isGuest;
  const mine = trpc.sayings.list.useQuery(undefined, { enabled: canSpeak && open });

  const remainingMs = useCooldown(sentAtMs, open);

  return (
    <>
      <ActionButton
        testId="sayings-button"
        label="Say"
        description="Say something to the room"
        tone="neutral"
        size={size}
        /*
         * The one control in the action row that is a **menu opener** rather than a game action,
         * and therefore the one that takes its press on the tap *completing* — see
         * `ActionButtonTrigger`. Opening on `pointerdown` put the dialog under a finger that was
         * still down, and the `pointerup`/`click` ending that same tap landed on the backdrop and
         * closed it again: "I have to push and hold Say for it to stay on screen". A split has a
         * 20 Hz deadline and must stay on `pointerdown`; a menu has none.
         *
         * A `tap` control has no press on the way down to repeat from either, which is right
         * anyway: a saying is a choice rather than a cascade, and `ActionQueue.say` is a one-slot
         * queue for the same reason.
         */
        trigger="tap"
        // A toggle, because there is no backdrop left to dismiss it with: the ways out are the
        // ✕, Escape, picking something, and the button that opened it.
        onPress={() => setOpen((wasOpen) => !wasOpen)}
        buttonRef={buttonRef}
      />
      <ArenaPanel
        open={open}
        onClose={() => setOpen(false)}
        portal
        title="Say something"
        description={
          canSpeak
            ? 'One line over your blob. Everybody nearby sees it.'
            : 'Sayings are for registered players.'
        }
        /*
         * ABOVE THE BUTTON THAT OPENED IT, AND THAT REVERSES WHAT THIS COMMENT USED TO SAY.
         *
         * It opened bottom-centre on every surface, on the reasoning that the Say button is drawn
         * in two different places — the touch action row and the HUD's bottom-left column — and
         * that only one of them has room to hang a panel off, so a fixed place clearing both was
         * the honest answer. The player's reading: "the predefined list should pop up above say so
         * the user doesn't have to come to the middle of the screen to select one." They are
         * right, and the reasoning was only ever true of a panel positioned by a *stylesheet*: the
         * button knows where it is at runtime, so the panel is measured against it and the fixed
         * place is not needed at all. `hud/anchor.ts` is that arithmetic, pure and asserted.
         *
         * The classes below are the fallback for the one frame before a measurement exists, and
         * for jsdom, which lays nothing out — see `isMeasured`. `style` wins over them when there
         * is a rect, which is why the two can coexist rather than one being dead.
         *
         * The compact cap is still read off `useCompactHud` rather than off `sm:`, and the
         * difference is still a real one: a phone on its side is 844 pixels *wide* and 390 tall,
         * so a `min-width` breakpoint fired the desktop's 24rem cap on a screen 390 pixels high
         * and the panel became the whole game. The anchored path narrows it further, to the room
         * that is actually above the button. See `game/hud/layout.ts`.
         */
        className={`fixed z-50 w-72 max-w-[calc(100vw-1.5rem)] ${
          at === null
            ? `left-1/2 -translate-x-1/2 ${compact ? 'bottom-20 max-h-[52dvh]' : 'bottom-32 max-h-[24rem]'}`
            : ''
        }`}
        style={at ?? undefined}
      >
        {canSpeak ? (
          <SayingList
            rows={mine.data?.list ?? []}
            loading={mine.isLoading}
            error={mine.error?.message ?? null}
            remainingMs={remainingMs}
            onPick={(sayingId) => {
              onSay(sayingId);
              // Closed on the press, not on an acknowledgement — there is no ack for a saying and
              // there is not going to be one. The cooldown on the button is the feedback.
              setOpen(false);
            }}
          />
        ) : (
          <GuestGate />
        )}
      </ArenaPanel>
    </>
  );
}

/**
 * Milliseconds left of the cooldown, or 0.
 *
 * A timer rather than a single render because the number counts down in front of the player, and
 * it runs only while the dialog is open and a pick is actually cooling — a closed picker costs no
 * interval at all.
 */
/**
 * The panel's measured position, or null while there is nothing to measure.
 *
 * A layout effect rather than an effect, because the panel is already on the screen by the time
 * an ordinary effect runs and a frame of it in the fallback position is a visible jump. It reads
 * the *button*, which is mounted the whole time and therefore measurable on the same tick the
 * panel opens.
 *
 * It re-measures on a resize and on an orientation change, and deliberately on nothing else. The
 * button does not move while the panel is open — the HUD zone it sits in is fixed, the panel
 * takes no space in the flow, and the only things that relocate it are the two events below.
 * A `ResizeObserver` on the button would fire on every re-render of a row that also holds a
 * cooldown countdown, for a rectangle that has not moved.
 */
function useAnchoredAbove(
  buttonRef: RefObject<HTMLButtonElement | null>,
  open: boolean,
  compact: boolean,
): AnchorPosition | null {
  const [at, setAt] = useState<AnchorPosition | null>(null);

  const measure = useCallback(() => {
    const button = buttonRef.current;
    if (button === null) return setAt(null);
    const box = button.getBoundingClientRect();
    const anchor = { left: box.left, top: box.top, width: box.width };
    // jsdom lays nothing out and neither has the browser on the very first paint of a route.
    // The caller keeps its centred classes for exactly this, which is better than pinning the
    // panel to the top-left corner off a rectangle of zeroes.
    if (!isMeasured(anchor)) return setAt(null);
    setAt(
      anchorAbove(
        anchor,
        PANEL_WIDTH_PX,
        { width: window.innerWidth, height: window.innerHeight },
        {
          gap: PANEL_GAP_PX,
          margin: PANEL_MARGIN_PX,
          // The desktop's own cap. On a phone the room above the button is far less than this and
          // is what actually decides the height — see `anchorAbove`.
          maxHeight: compact ? COMPACT_PANEL_MAX_PX : ROOMY_PANEL_MAX_PX,
        },
      ),
    );
  }, [buttonRef, compact]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [open, measure]);

  return open ? at : null;
}

function useCooldown(sentAtMs: number | null, open: boolean): number {
  const [nowMs, setNowMs] = useState(() => Date.now());

  const endsAtMs = sentAtMs === null ? 0 : sentAtMs + SAYING_COOLDOWN_MS;
  const cooling = open && endsAtMs > nowMs;

  useEffect(() => {
    if (!cooling) return;
    const timer = setInterval(() => setNowMs(Date.now()), COOLDOWN_TICK_MS);
    return () => clearInterval(timer);
  }, [cooling]);

  // Re-read on every render as well as on the tick: the dialog opening is a render, and it must
  // not show a countdown that stopped where the last one left off.
  return Math.max(0, endsAtMs - Math.max(nowMs, Date.now()));
}

/** One entry of a player's own vocabulary, as the picker draws it. */
interface SayingRow {
  id: string;
  text: string;
  custom: boolean;
}

function SayingList({
  rows,
  loading,
  error,
  remainingMs,
  onPick,
}: {
  rows: readonly SayingRow[];
  loading: boolean;
  error: string | null;
  remainingMs: number;
  onPick: (sayingId: string) => void;
}) {
  const cooling = remainingMs > 0;

  return (
    <div className="space-y-2">
      {error !== null && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {cooling && (
        <p
          data-testid="saying-cooldown"
          role="status"
          className="rounded-md bg-secondary/60 px-2 py-1 text-xs text-muted-foreground"
        >
          Sent. You can say something else in {Math.ceil(remainingMs / 1000)}s.
        </p>
      )}

      {loading ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Loading your sayings…</p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">
          Your list is empty. Edit it on your profile.
        </p>
      ) : (
        <ul data-testid="saying-options" className="space-y-1">
          {rows.map((row) => (
            <SayingOption key={row.id} row={row} cooling={cooling} onPick={onPick} />
          ))}
        </ul>
      )}

      <p className="px-1 text-[11px] text-muted-foreground">
        {/* A plain anchor rather than a router `Link`, for `RunStanding`'s reason: this is drawn
            inside the arena overlay, which is the one subtree in the client that has to be
            renderable on its own, and following it tears the arena down either way. */}
        Add your own on your{' '}
        <a href="/profile" className="underline underline-offset-2">
          profile
        </a>
        . New ones are checked by a moderator first.
      </p>
    </div>
  );
}

/**
 * What a guest — or a signed-out player — is shown instead.
 *
 * The catalogue is still drawn, and every entry is inert. "Here is what you are missing" is the
 * whole point of showing the feature to somebody who cannot use it, and it costs nothing:
 * `DEFAULT_SAYINGS` is a shared constant, so this list needs no query and would be the same words
 * the server resolves. What it cannot show is the other half of a real player's vocabulary —
 * their own approved submissions — which is the honest reason to register rather than a hook.
 *
 * Never a silent failure: the reason and the way out are on the same screen as the refusal.
 */
function GuestGate() {
  return (
    <div data-testid="sayings-guest-gate" className="space-y-3">
      <p className="text-sm text-muted-foreground">
        A guest account is remembered by this browser alone, so it is not an identity to hang
        moderated words on. Create an account and this list is yours — re-ordered how you like, and
        with your own sayings on it once a moderator has passed them.
      </p>
      <a href="/register" className={buttonVariants({ size: 'sm' })}>
        Register to use sayings
      </a>
      <ul data-testid="saying-options" className="space-y-1">
        {DEFAULT_SAYINGS.map((saying) => (
          <li key={saying.id}>
            <button
              type="button"
              disabled
              data-testid={`saying-option-${saying.id}`}
              className="w-full cursor-not-allowed rounded-md px-2 py-2 text-left text-sm opacity-40"
            >
              {saying.text}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * One pickable saying.
 *
 * **A ROW IS ACTIVATED ON `pointerup`, NOT ON `click`, AND IT IS ITS OWN COMPONENT FOR THAT
 * REASON ALONE** — `useTapActivate` is a hook and cannot be called inside a `map`. The player
 * reported the Say *button* being dead with a thumb on the joystick, that was fixed, and then
 * the rows inside the panel it opens turned out to have the identical problem: "if I try to
 * select a saying with my thumb down, that fails until I lift thumb up". A click on a touch
 * screen comes from a tap gesture the platform will not report while another finger is down.
 * See `game/hud/tap-activate.ts`.
 */
function SayingOption({
  row,
  cooling,
  onPick,
}: {
  row: SayingRow;
  cooling: boolean;
  onPick: (sayingId: string) => void;
}) {
  const activate = useTapActivate(() => onPick(row.id), cooling);
  return (
    <li>
      <button
        type="button"
        data-testid={`saying-option-${row.id}`}
        disabled={cooling}
        {...activate}
        className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-secondary/60 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <span className="min-w-0 flex-1 break-words">{row.text}</span>
        {/* Which half of the vocabulary it came from. A player who got one approved should
                    be able to find it, and this is cheaper than a second group. */}
        {row.custom && (
          <span className="shrink-0 rounded-full border border-primary/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-primary">
            Yours
          </span>
        )}
      </button>
    </li>
  );
}
